import type {
  DomainPropertySummary,
  DomainPropertyIR,
  ManifestEnumRuleInput,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

function renderInlineStringUnion(values: string[]) {
  return values.map((value) => JSON.stringify(value)).join(" | ");
}

function normalizeEnumInput(enumSpec?: string[] | ManifestEnumRuleInput) {
  if (!enumSpec) return null;
  const config = Array.isArray(enumSpec) ? { values: enumSpec } : enumSpec;
  const values = (config.values || []).filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
  if (values.length === 0) return null;
  return config.message ? { values, message: config.message } : { values };
}

export function buildEnumFieldMutations(
  fieldPath: string,
  enumSpec?: string[] | ManifestEnumRuleInput,
) {
  const normalized = normalizeEnumInput(enumSpec);
  if (!normalized) return [];

  const messageArg = normalized.message
    ? `, message: ${JSON.stringify(normalized.message)}`
    : "";

  return [
    {
      label: `set enum on ${fieldPath}`,
      query: `mutation { at(path: ${JSON.stringify(fieldPath)}) { set_enum(values: ${JSON.stringify(
        normalized.values,
      )}${messageArg}) { values } } }`,
    },
  ];
}

export const enumMetamodelPackage = defineMetamodelPackage({
  id: "enum",
  docs: {
    fieldRows: [
      {
        key: "enum",
        description:
          'Allowed values. Accepts `["a", "b"]` or `{ "values": [...], "message": "..." }`.',
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type EnumMetamodel {
        model: Model!
        values: [String!]!
        message: String
      }

      extend type Model {
        enum_rule: EnumMetamodel
      }

      extend type ModelMutation {
        set_enum(values: [String!]!, message: String): EnumMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        EnumMetamodel: {
          model: (value) => value.model,
          values: (value) => value.values,
          message: (value) => value.message || null,
        },
        Model: {
          enum_rule: async (ant) => await run(ant.enum_rule()),
        },
        ModelMutation: {
          set_enum: async (ant, { values, message }) => {
            const model = await run(ant.set_enum(values, message));
            return { model, values, message };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildEnumFieldMutations(fieldPath, spec.enum);
    },
  },
  summary: {
    selections: {
      propertyFields: [`enum_rule { values message }`],
    },
    readPropertySummary(rawProperty) {
      const values = Array.isArray(rawProperty.enum_rule?.values)
        ? rawProperty.enum_rule.values.filter(
            (value: unknown): value is string =>
              typeof value === "string" && value.length > 0,
          )
        : [];
      if (values.length === 0) return { enumRule: null };
      const message =
        typeof rawProperty.enum_rule?.message === "string"
          ? rawProperty.enum_rule.message
          : null;
      return {
        enumRule: message ? { values, message } : { values },
      };
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      propertySummary: DomainPropertySummary,
    ) {
      const values = propertySummary.enumRule?.values || [];
      if (values.length === 0) return propertyIR;

      return {
        ...propertyIR,
        type: renderInlineStringUnion(values),
        docs: [
          ...propertyIR.docs,
          `Allowed values: ${values.join(", ")}.`,
          ...(propertySummary.enumRule?.message
            ? [propertySummary.enumRule.message]
            : []),
        ],
      };
    },
  },
});
